import styled from "@emotion/styled";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../redux/root";
import { fetchReferralSocialBonuses as fetchReferralSocialBonusesAction } from "../../redux/team";
import { analyticsEvent } from "@util/analytics";
import { getReferralUrlForApp } from "@util/appUrls.ts";

import CopyField from "../revamped/form/CopyField";
import Modal from "../revamped/Modal";
import { Heading2, RegularText } from "../revamped/Typography";
import { ReferralIcon } from "@alchemy/ui";
import ReferralInviteField from "./ReferralInviteField";
import ReferralModalBanner from "./ReferralModalBanner";

const StyledIcon = styled(ReferralIcon)`
  margin: 6px;
  transform: scale(1.11);
`;

const StyledTitle = styled(Heading2)`
  font-weight: 600;
  line-height: 100%;

  @media (min-width: 428px) {
    font-size: 32px;
  }
`;

const StyledModal = styled(Modal)`
  color: #000;
`;

const StyledText = styled(RegularText)`
  margin-top: 12px;
  color: #414348;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 40px;
  margin-bottom: 32px;
`;

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: string;
}

const ReferralModal = ({ isOpen, onClose, source }: ReferralModalProps) => {
  const dispatch = useDispatch();
  const referralSocialBonuses = useSelector(
    (state: RootState) => state.team.referralSocialBonuses,
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchReferralSocialBonusesAction());

      analyticsEvent("Referral: Opened Referral modal", { source });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [isOpen]);

  const handleCopy = useCallback(() => {
    analyticsEvent("Referral: Copied referral link");
  }, []);

  return (
    <StyledModal
      isOpen={isOpen && referralSocialBonuses.hasValue}
      onClose={onClose}
    >
      <Header>
        <StyledIcon />

        <StyledTitle>Give $100, Get $100</StyledTitle>

        <StyledText>
          Gift your friends $100 worth of Alchemy credits through email
          invitation or your link. Receive $100 credits for every friend who
          upgrades to a paid plan.
        </StyledText>
      </Header>

      <Fields>
        <ReferralInviteField />

        <CopyField
          label="Share your referral link"
          value={getReferralUrlForApp()}
          buttonProps={{ onClick: handleCopy }}
        />
      </Fields>

      <ReferralModalBanner onCloseModal={onClose} />
    </StyledModal>
  );
};

export default ReferralModal;
